import { useState, useMemo, useEffect } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import sortBy from "lodash/sortBy";

import Multimedia from "@sharedComponents/multimedia";
import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";

import WeglotNoTranslationWrapper from "@stadium/services/weglot/no-translation-wrapper";

import AuthLogin from "@components/auth/buttons/login";
import { useAuth0V2 } from "@stadium/services/auth0";

import { isCustomCurrency } from "@stadium/helpers/currency";
import { pluralizeWord } from "@stadium/helpers/string";
import {
  getQuantityFromSizeMapping,
  getTotalFromSizeMapping,
  getProductColorsAndSizes,
} from "@helpers/product-details";

import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import { getTaxons } from "@selectors/config";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { InternalButton } from "@sharedComponents/buttons";
import { formatPrice } from "@helpers/currency";
import { getRestrictionIdsString, getPricingMatrix } from "@helpers/orders/cart";
import {
  isByoProduct,
  isCollectionTypeProduct,
  isSwagProduct,
  isMedleyProduct,
} from "@helpers/products/types";
import { getProductRoute } from "@constants/routes";
import { PRICING_MATRIX_PRICE_ID, MINIMUM_CART_ITEM } from "@constants/orders/cart";
import { US_CURRENCY_CODE } from "@constants/store";

import { useStoreContext } from "@components/store/provider";
import QuantitySelector from "@components/store/product-detail/shared/quantity-selector";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import useShowPrefrenceRestrictionModal from "@hooks/product-detail/use-show-prefrence-restriction-modal";

import { useCartListingContext } from "@contexts/cart-listing-context";
import { useSendItemsContext } from "@contexts/send-items-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";

import { getCartOrder } from "@selectors/cart";
import {
  getIsPricelessShop,
  getStoreCurrencyPrefix,
  getStoreCurrencySuffix,
} from "@selectors/store";
import { getCountry } from "@selectors/country";
import { getCountriesGroupedByZoneUsingCountryZoneId } from "@stadium/redux-utils/selectors/configSelector";

import { weglotNoTranslationClassName } from "@stadium/constants";
import { NO_RESTRICTION_OPTION } from "@stadium/constants/products";
import {
  ANALYTICS_EVENT_ACTION_REMOVED,
  ANALYTICS_EVENT_ACTION_UPDATED,
} from "@stadium/services/analytics/constants/default";
import { ITEM_EDIT_TARGET, ITEM_TARGET } from "@constants/analytics/cart";

import style from "./main.module.scss";
import NoImage from "@assets/images/no-image.svg";

const AssetCustomization = dynamic(() => import("@components/shared/cart/asset-customization"));

const emptyObj = {};

const CART_IMAGE_DIMENSIONS = {
  height: 128,
  width: 128,
};

const NEW_SWAGMAGIC_CART_IMAGE_DIMENSIONS = {
  height: 134,
  width: 134,
};

const LineItemCard = ({
  lineItem,
  removeProductFromCart,
  onChangeQuantity,
  editable,
  disabled,
  intent,
  getUpdatedItem,
  zoneId = "",
  hidePrices = false,
  orderCurrencyCode,
  hideLink = false,
  isCustomizable = false,
  onUpdateLogo = () => {},
}) => {
  const {
    id,
    variantLineItemIds,
    images,
    name,
    restaurantName: brandName,
    price,
    restrictions,
    dietaryRestriction,
    dietaryPreference = "",
    slug,
    quantity,
    optionValues,
    medleyComponents,
    sizeQtyMapping,
    minOrderableQuantity,
    masterVariant,
    isBulkAvailable,
    intent: productIntent,
    isMaster = false,
    askForRestriction = false,
    askForPreference = false,
    isSwagVariantSelectionAvailable = false,
    swagVariantFromCatalogStore = [],
    productData,
    isRestrictionsSelected = false,
    product,
  } = lineItem;

  const withAnalytics = useWithAnalytics();
  const { isSendToMyself, isSendToOthers } = useSendItemsContext();

  const removeItemAnalyticsPayload = {
    track: true,
    target: ITEM_TARGET,
    action: ANALYTICS_EVENT_ACTION_REMOVED,
    eventProperties: { product_id: lineItem.productId },
  };

  const productDetails = productData
    ? {
        variantsIncludingMaster: swagVariantFromCatalogStore,
        optionTypes: productData.optionTypes,
      }
    : product;

  const { colorOptionsValue, sizeOptionsValue } =
    isSendToOthers && isSwagProduct(productIntent)
      ? getProductColorsAndSizes(productDetails)
      : emptyObj;

  const sortedSizeOptionValue = useMemo(
    () => sortBy(sizeOptionsValue, ({ position }) => position),
    [sizeOptionsValue],
  );

  const sortedColorOptionValue = useMemo(
    () => sortBy(colorOptionsValue, ({ position }) => position),
    [colorOptionsValue],
  );

  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();

  const { previewMode } = usePreviewModeContext();

  const { isAuthenticated } = useAuth0V2();

  const [collectionProductSelectionError, setCollectionProductSelectionError] = useState(emptyObj);

  const cartOrder = useSelector(getCartOrder);
  const selectedCountry = useSelector(getCountry);
  const countriesGroupedByZone = useConfig(getCountriesGroupedByZoneUsingCountryZoneId);

  const skipMinimumOrderableQuantity = cartOrder?.skipMinimumOrderableQuantity;

  const { storeBasePathWithCountryPathKey, storeDisplayCurrencyCode, isNewSwagmagicHost } =
    useStoreContext();

  const countryCode =
    useMemo(
      () => (isSendToMyself ? selectedCountry?.iso : countriesGroupedByZone[zoneId]?.[0]?.iso),
      [isSendToMyself, selectedCountry, countriesGroupedByZone, zoneId],
    ) || "";
  const storeBasePath = storeBasePathWithCountryPathKey.replace(
    ":countryCode",
    countryCode.toLowerCase(),
  );
  const [showByoComponents, setShowByoComponents] = useState(false);
  const showRestrictions = isRestrictionsSelected || !isEmpty(dietaryRestriction);

  const isOrderCurrencyUSD = orderCurrencyCode === US_CURRENCY_CODE;
  const storeCurrencyPrefix = useSelector(getStoreCurrencyPrefix);
  const storeCurrencySuffix = useSelector(getStoreCurrencySuffix);
  const currencyPrefix = isOrderCurrencyUSD ? "" : storeCurrencyPrefix;
  const currencySuffix = isOrderCurrencyUSD ? "" : storeCurrencySuffix;
  const spreeTaxons = useConfig(getTaxons);
  const isPricelessShop = useSelector(getIsPricelessShop);

  const { openSwagVariantSelectorModal, setVariantSelectionError, variantSelectionError } =
    useCartListingContext();

  const {
    renderRestrictionsModal,
    renderExistingCartItemSelectOptionBtn,
    renderMedlyDietryRestrictionsModal,
  } = useShowPrefrenceRestrictionModal({
    product: lineItem,
    selectedVariant: { externalId: variantLineItemIds },
  });

  const productRoute = getProductRoute({ productSlug: slug, storeBasePath });
  const hasSizeQtyMapping = Object.keys(sizeQtyMapping || emptyObj).length > 0;
  const totalPrice = hasSizeQtyMapping ? getTotalFromSizeMapping(sizeQtyMapping) : price * quantity;
  const itemsCount = hasSizeQtyMapping ? getQuantityFromSizeMapping(sizeQtyMapping) : quantity;
  const zoneIdPayload = (!isSendToMyself ? zoneId : selectedCountry?.zoneId) || "";

  const getUpdatedPriceParams = (quantity) => {
    const productSupportsPriceMatrix = isBulkAvailable || masterVariant?.prices?.length > 1;
    if (!productSupportsPriceMatrix) return { price: lineItem.price };
    return {
      price: getPricingMatrix({ variant: masterVariant }).find(({ isInRange }) =>
        isInRange(quantity),
      )?.[PRICING_MATRIX_PRICE_ID],
    };
  };

  const renderRestrictions = (restrictionId) => {
    const restrictionData = spreeTaxons[restrictionId];
    if (!restrictionData) return null;
    const { id, name, imageUrl } = spreeTaxons[restrictionId];
    return (
      <span className={style.boxSizeItem} key={id}>
        {imageUrl?.mini && <img src={imageUrl.mini || NoImage} alt="" width="16px" />}
        {name}
      </span>
    );
  };

  const renderDietaryRestriction = (restrictionKey) => {
    return (
      <span className={style.boxSizeItem} key={restrictionKey}>
        {restrictionKey}
      </span>
    );
  };

  const getVendorId = () => {
    if (isSwagProduct(productIntent)) {
      return product?.vendorByCountry?.externalId ?? productData?.vendorByCountry?.externalId;
    }
    return null;
  };

  const onChange = (value) => {
    if (isByoProduct(intent)) {
      const updatedQuantityObject = {
        medleyItemSetId: lineItem.id,
        quantity: value,
        zoneId: zoneIdPayload,
      };
      onChangeQuantity(updatedQuantityObject, intent);
    } else {
      const vendorId = getVendorId();
      const updatedQuantityObject = {
        productId: lineItem.productId,
        intent,
        quantity: value,
        ...getUpdatedPriceParams(value),
        storeId: lineItem.storeId,
        dietaryPreference,
        isBulkAvailable,
        zoneId: zoneIdPayload,
        vendorId,
      };
      if (isCollectionTypeProduct(intent)) {
        updatedQuantityObject.orderProductSetId = id;
        updatedQuantityObject.orderProductSetRestrictionId = getRestrictionIdsString(lineItem);
      }
      onChangeQuantity(updatedQuantityObject, intent);
    }
  };

  const renderQuantity = () => {
    if (editable)
      return (
        <QuantitySelector
          minOrderableQuantity={skipMinimumOrderableQuantity ? 1 : minOrderableQuantity}
          quantity={quantity}
          disabled={disabled}
          setQuantity={onChange}
          decreaseBelowMinAllowed={false}
          onZeroHandler={() => {
            withAnalytics(undefined, removeItemAnalyticsPayload)();
            removeProductFromCart({ lineItem, intent, zoneId: zoneIdPayload });
          }}
          className={`defaultStyle ${weglotNoTranslationClassName}`}
          eventLocationAnalytics={{
            track: true,
            action: ANALYTICS_EVENT_ACTION_UPDATED,
            eventProperties: { product_id: lineItem.productId },
          }}
        />
      );
    return (
      <div className={style.qty}>
        Qty: <WeglotNoTranslationWrapper>{quantity}</WeglotNoTranslationWrapper>
      </div>
    );
  };

  const renderCloseBtn = () => {
    if (editable) {
      let className = style.removeItem;
      className += disabled ? " disabled" : "";
      return (
        <InternalButton
          styleType="no-style"
          className={className}
          onClick={() => {
            removeProductFromCart({ lineItem, intent, zoneId: zoneIdPayload });
          }}
          eventLocationAnalytics={removeItemAnalyticsPayload}
        ></InternalButton>
      );
    }
  };
  const renderEditBtn = () => {
    if (editable) {
      const editProductIds = encodeURIComponent(variantLineItemIds?.join(",") || id);

      const editProductRoute = getProductRoute({
        productSlug: slug,
        storeBasePath,
        editProductIds,
      });
      let className = style.editItem;
      return (
        <Link
          href={editProductRoute}
          title="Edit"
          className={className}
          prefetch={false}
          onClick={() =>
            withAnalytics(undefined, {
              track: true,
              target: ITEM_EDIT_TARGET,
              eventProperties: { product_id: lineItem.productId },
            })()
          }
        ></Link>
      );
    }
  };

  const getFormattedPrice = (price) => {
    if (orderCurrencyCode && isCustomCurrency(orderCurrencyCode)) {
      const code = isOrderCurrencyUSD ? orderCurrencyCode : storeDisplayCurrencyCode;
      return formatPrice(price, {
        prefix: currencyPrefix,
        currencyCode: code,
      });
    }
    return formatPriceInDisplayStoreCurrency(price, orderCurrencyCode);
  };

  const renderOptionValues = ({ id, presentation, optionType }, index) => {
    const isOptionTypeColor = optionType?.key === "color";
    const parentDivClassName = isOptionTypeColor ? style.liaColorRow : "";

    return (
      <span className={`${parentDivClassName} ${style.onlyColorWithName}`}>
        <span
          className={`${style.dietryChoice} ${isOptionTypeColor ? style.colorChoice : ""}`}
          key={id}
        >
          {isOptionTypeColor ? `${optionType?.name}: ${presentation}` : `${presentation}`}
        </span>
        {!index == optionValues.length - 1 && <span className={style.choiceDivider}>|</span>}
      </span>
    );
  };

  const renderPerUnitValue = () => (
    <div className={style.lockerItemAmount}>
      <WeglotNoTranslationWrapper>
        {getFormattedPrice((totalPrice / quantity).toFixed(2))}
      </WeglotNoTranslationWrapper>
      {currencySuffix && <small>{currencySuffix}</small>}/unit
    </div>
  );

  const renderDietaryPreferences = () =>
    dietaryPreference && (
      <div className={style.preferenceWrapper}>
        <span className={style.preferenceLabel}>Preferences:</span>{" "}
        <span className={style.preferenceDetail}>{dietaryPreference}</span>
      </div>
    );

  const renderRestrictionsSection = () =>
    restrictions?.length ? (
      restrictions.map(renderRestrictions)
    ) : (
      <span className={style.boxSizeItem}>{NO_RESTRICTION_OPTION?.name}</span>
    );

  const renderDefaultOptions = () => (
    <>
      <div className={style.optionsBlock}>
        {isSendToMyself &&
          [...(optionValues || [])]
            .reverse()
            .map((value, index) => renderOptionValues(value, index))}
        {renderVariantSelectOption()}
      </div>
      {showRestrictions && (
        <div className={style.boxSizeList}>
          <span className={style.boxDetail}>BOX DETAILS:</span>{" "}
          {isRestrictionsSelected && renderRestrictionsSection()}
          {dietaryRestriction.map(renderDietaryRestriction)}
        </div>
      )}
      {renderDietaryPreferences()}
      <div className={style.qtyParentBlock}>{renderQuantity()}</div>
    </>
  );

  const renderSizeQtyMappingOptions = () => {
    const { color, quantity } = lineItem;

    return (
      <div className={style.colorWithQty}>
        {Boolean(color) && isSendToMyself && (
          <div className={style.liaColorRow}>
            <span>COLOR: </span>
            <span className={style.spaceBtwColor}> {` ${color}`} </span>
          </div>
        )}
        <span className={`${style.liQtyRow} ${style.liQtyRowFirst}`}>
          <span className={style.qtyBlock}>
            <span className={style.qtyLabel}>Total qty:</span> {quantity}
          </span>
        </span>
        <span className={`${style.liQtyRow} ${style.liQtyRowLast}`}>
          {hasSizeQtyMapping && isSendToMyself && (
            <span className={style.pillBlock}>
              {Object.keys(sizeQtyMapping).map((s) => (
                <span key={s} className={style.sizePill}>{`${s}:${sizeQtyMapping[s].qty}`}</span>
              ))}
            </span>
          )}
        </span>
        {renderDietaryPreferences()}
      </div>
    );
  };

  const renderOptionsAndQty = () => {
    if (hasSizeQtyMapping) {
      return renderSizeQtyMappingOptions();
    }
    return renderDefaultOptions();
  };

  // This block has been added whilen switching from send to others to send to myself and variant and preference required for the product
  const isSelectOptionErrorRequired =
    isSendToMyself &&
    editable &&
    (isSwagProduct(productIntent) || isCollectionTypeProduct(productIntent));

  const omitSwagLineItemError = () =>
    setVariantSelectionError({ ...omit(variantSelectionError, id) });
  const omitCollectionLineItemError = () => {
    setCollectionProductSelectionError({ ...omit(collectionProductSelectionError, id) });
  };

  useEffect(() => {
    if (!isSelectOptionErrorRequired) return;

    const setSwagLineItemError = () => {
      if (!variantSelectionError[id])
        setVariantSelectionError({ ...variantSelectionError, [id]: true });
    };
    const setCollectionItemError = () => {
      if (!collectionProductSelectionError[id])
        setCollectionProductSelectionError({ ...collectionProductSelectionError, [id]: true });
    };
    const isSwagProductError =
      isSwagProduct(productIntent) &&
      isMaster &&
      (isSwagVariantSelectionAvailable ||
        (!isSwagVariantSelectionAvailable && askForPreference && isEmpty(dietaryPreference)));

    const isCollectionProductError =
      isCollectionTypeProduct(productIntent) &&
      (askForRestriction || askForPreference || isMedleyProduct(productIntent)) &&
      isEmpty(dietaryRestriction) &&
      isEmpty(dietaryPreference) &&
      !isRestrictionsSelected;

    if (isSwagProductError) return setSwagLineItemError();
    if (isCollectionProductError) return setCollectionItemError();
    if (!isEmpty(variantSelectionError) && variantSelectionError[id])
      return omitSwagLineItemError();
    if (!isEmpty(collectionProductSelectionError) && collectionProductSelectionError[id])
      omitCollectionLineItemError();
  }, [id, variantSelectionError]);

  const renderVariantSelectOption = () => {
    if (!isSelectOptionErrorRequired || previewMode) return;

    const isSwagSelectOptionErrorRequired =
      isSwagProduct(productIntent) && variantSelectionError[id];
    const isCollectionSelectOptionErrorRequired =
      isCollectionTypeProduct(productIntent) && collectionProductSelectionError[id];

    if (
      !isAuthenticated &&
      (isSwagSelectOptionErrorRequired || isCollectionSelectOptionErrorRequired)
    )
      return (
        <AuthLogin loginOptions={{ signUp: true }}>
          <InternalButton styleType="link" className={style.selectBtn}>
            Select option to continue
          </InternalButton>
        </AuthLogin>
      );

    if (isSwagSelectOptionErrorRequired) {
      return (
        <InternalButton
          onClick={openSwagVariantSelectorModal}
          styleType="link"
          className={style.selectBtn}
        >
          Select option to continue
        </InternalButton>
      );
    }

    if (isCollectionSelectOptionErrorRequired) {
      const restrictioModalParam = {
        lineItem,
        isUpdateExistingItemInCart: true,
        onSuccessCallback: omitCollectionLineItemError,
      };
      return (
        <>
          {renderExistingCartItemSelectOptionBtn()} {renderRestrictionsModal(restrictioModalParam)}
          {isMedleyProduct(productIntent) &&
            renderMedlyDietryRestrictionsModal(restrictioModalParam)}
        </>
      );
    }
  };

  const renderImage = () => {
    const dimensions = isNewSwagmagicHost
      ? NEW_SWAGMAGIC_CART_IMAGE_DIMENSIONS
      : CART_IMAGE_DIMENSIONS;
    return (
      <Multimedia
        type="image"
        src={images?.[0] || NoImage}
        alt="Not Found"
        height={dimensions.height}
        width={dimensions.width}
        className={!images?.[0] ? style.imageNotAvailable : style.imageAvailable}
      />
    );
  };

  const renderImageSection = () => {
    if (hideLink) return renderImage();
    return (
      <Link href={productRoute} prefetch={false}>
        {renderImage()}
      </Link>
    );
  };

  const renderColorOptions = () =>
    Boolean(sortedColorOptionValue?.length) && (
      <div className={style.colorCodeContainer}>
        <h6 className={style.subHeading}>
          Available {pluralizeWord("color", { count: sortedColorOptionValue.length })}:
        </h6>
        <div className={style.colorCodeCircles}>
          {sortedColorOptionValue.map(({ name, id, presentation }) => {
            return (
              <ToolTip content={presentation} arrow isLightTheme key={id}>
                <div className={style.colorContainer} title={presentation}>
                  <div key={name} className={style.colorCircle} style={{ backgroundColor: name }} />
                </div>
              </ToolTip>
            );
          })}
        </div>
      </div>
    );

  const renderSizeOptions = () => {
    if (!sortedSizeOptionValue?.length) {
      return;
    }
    const availableSizes = sortedSizeOptionValue.map(({ presentation }) => presentation).join(", ");

    return (
      <div className={style.sizesContainer}>
        <div className={style.sizeBlock}>
          <h6 className={style.subHeading}>
            Available {pluralizeWord("size", { count: sortedSizeOptionValue.length })}:
          </h6>
        </div>
        <span className={style.availableSizes}>{availableSizes}</span>
      </div>
    );
  };

  return (
    <>
      <dd
        className={`${style.cartItem} ${medleyComponents ? style["cartItemWithBYO"] : ""} ${
          isNewSwagmagicHost ? style.cartItemV2 : ""
        }`}
        key={id}
      >
        <div className={style.imageBox}>{renderImageSection()}</div>

        <div className={style.itemDetailBox}>
          <span className={style.itemBrand}>{brandName}</span>
          <span className={style.byoPill}>{isByoProduct(intent) && "BUILD-YOUR-OWN"}</span>
          <div className={style.itemWithpoint}>
            <div className={style.itemName}>
              {hideLink ? (
                <span>{name}</span>
              ) : (
                <Link href={productRoute} legacyBehavior prefetch={false}>
                  {name}
                </Link>
              )}
            </div>
            {!(hidePrices || isPricelessShop) && (
              <div className={style.pointBlock}>
                {itemsCount > MINIMUM_CART_ITEM && renderPerUnitValue()}
                <div className={style.pointBlockData}>
                  <WeglotNoTranslationWrapper>
                    {getFormattedPrice(totalPrice)}
                  </WeglotNoTranslationWrapper>
                  {currencySuffix && <span className={style.pointsText}>{currencySuffix}</span>}
                </div>
              </div>
            )}
          </div>
          {renderColorOptions()}
          {renderSizeOptions()}
          {renderOptionsAndQty()}
          <div className={style.editWithRemove}>
            {renderEditBtn()}
            {renderCloseBtn()}
          </div>
        </div>
        <div className={style.byoBlock}>
          <div
            className={`${style.byoItemsBlock} ${
              showByoComponents ? style["BYOlistExpend"] : style["BYOlistCollapse"]
            }`}
          >
            {medleyComponents?.map((item) => {
              const { id, name, quantity, price, images, optionValues = [] } = getUpdatedItem(item);
              return (
                <div key={id} className={style.byoItem}>
                  <div className={style.imageBlock}>
                    <Multimedia
                      type="image"
                      src={images?.[0] || NoImage}
                      alt="Not Found"
                      height={128}
                      width={128}
                    />
                  </div>
                  <div className={style.descBlock}>
                    <div className={style.productName}>{name}</div>
                    <div className={style.points}>
                      <WeglotNoTranslationWrapper>
                        {getFormattedPrice(price)}
                      </WeglotNoTranslationWrapper>
                      {currencySuffix && <small>{currencySuffix}</small>} (
                      {!isPricelessShop &&
                        optionValues.map((value, index) => renderOptionValues(value, index))}
                      )
                    </div>
                  </div>
                  <div className={style.qty}>{quantity}</div>
                </div>
              );
            })}
          </div>
          {medleyComponents && (
            <div className={style.byoListItems}>
              <div
                onClick={() => setShowByoComponents(!showByoComponents)}
                className={style.collapseButton}
              >
                {showByoComponents ? "Collapse" : "See all items"}
              </div>
            </div>
          )}
        </div>
        {isCustomizable && (
          <AssetCustomization lineItem={lineItem} onUpdateLogo={onUpdateLogo} editable={editable} />
        )}
      </dd>
      {showRestrictions && (
        <div className={`${style.cartItemFooter} cartFooter`}>
          <div className={style.boxSizeList}>
            <span className={style.boxDetail}>BOX DETAILS:</span>{" "}
            {isRestrictionsSelected && renderRestrictionsSection()}
            {dietaryRestriction.map(renderDietaryRestriction)}
          </div>
        </div>
      )}
    </>
  );
};

export default LineItemCard;
